import { zodResolver } from "@hookform/resolvers/zod";
import { useRouter } from "next/navigation";
import React, { useCallback, useState } from "react";
import { useForm } from "react-hook-form";
import * as zod from "zod";
import Link from "next/link";
import PhoneFieldInput from "components/Quiz/components/PhoneFieldInput";
import TextFieldInput from "components/Quiz/components/TextFieldInput";
import { AddressAnswer } from "components/Quiz/quizTypes";
import { capitalizeName } from "components/Quiz/utils";
import AddressAutocomplete from "components/Tiles/commonComponents/AddressAutocomplete";
import { SELLER_ADDRESS_LOCALSTORAGE_KEY } from "components/Tiles/constants";
import useMediaQuery from "components/Tiles/hooks/useMediaQuery";
import { Button, ButtonVariantTypes } from "components/ui/button";
import { Form, FormField } from "components/ui/form";
import { RecordItem } from "types/common";
import { Button as UIButton } from "components/ui/button";

import Loader from "components/Loader";
import useErrorLogger from "hooks/useErrorLogger";
import { ERROR_TYPES } from "utils/constants";
import { APP_LANGUAGES, APP_STRINGS_LABELS, getAppStringForLanguage } from "utils/language";
import toast from "utils/toast";

type LeadFormProps = {
  visibleFields?: {
    name?: boolean;
    email?: boolean;
    phone?: boolean;
    address?: boolean;
  };
  redirectToQuiz?: boolean;
  webhookUrl?: string;
  quizSlug?: string;
  buttonText?: string;
  showTermsMessage?: boolean;
  language?: APP_LANGUAGES;
  redirectSlug?: string;
};

const getFormSchemaFromVisibleFields = (visibleFields?: LeadFormProps["visibleFields"]) => {
  const schemaObject: RecordItem = {};
  if (visibleFields?.name) {
    (schemaObject.firstName = zod
      .string({
        required_error: "First name is required"
      })
      .min(1, {
        message: "First name is required"
      })
      .transform(capitalizeName)),
      (schemaObject.lastName = zod
        .string({
          required_error: "Last name is required"
        })
        .min(1, {
          message: "Last name is required"
        })
        .transform(capitalizeName));
  }

  if (visibleFields?.email) {
    schemaObject.email = zod
      .string({
        required_error: "Email is required"
      })
      .min(1, {
        message: "Email is required"
      })
      .email({
        message: "Invalid email address"
      });
  }

  if (visibleFields?.phone) {
    schemaObject.phone = zod
      .string({
        required_error: "Phone number is required"
      })
      .min(1, {
        message: "Phone number is required"
      })
      .regex(/\+1[0-9]{10}$/, {
        message: "Invalid phone number"
      });
  }

  return zod.object(schemaObject);
};

const LeadForm = ({
  visibleFields = {},
  redirectToQuiz,
  webhookUrl,
  quizSlug,
  buttonText = "Get a Cash Offer",
  showTermsMessage = false,
  language = APP_LANGUAGES.ENGLISH,
  redirectSlug
}: LeadFormProps) => {
  const [address, setAddress] = useState<AddressAnswer>({});
  const [hasAddressError, setHasAddressError] = useState(false);
  const formSchema = getFormSchemaFromVisibleFields(visibleFields);
  const { logError } = useErrorLogger();
  const formState = useForm<zod.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: ""
    }
  });

  const { isMobile } = useMediaQuery();
  const router = useRouter();
  const onlyAddressFieldVisible =
    Object.values(visibleFields).filter((val) => val).length === 1 && visibleFields.address;

  const [isLoading, setIsLoading] = useState(false);
  const handleClick = useCallback(
    async (_?: React.MouseEvent<HTMLButtonElement>, newAddress?: AddressAnswer) => {
      if (isLoading) return;

      const addressToUse = newAddress ? newAddress : address;
      // if address is not filled, do not proceed
      if (visibleFields.address && !Object.values(addressToUse).some((val) => !!val)) {
        setHasAddressError(true);
        if (!onlyAddressFieldVisible) {
          return;
        }
      } else {
        setHasAddressError(false);
      }
      setIsLoading(true);
      if (visibleFields.address && typeof window !== "undefined") {
        localStorage.setItem(SELLER_ADDRESS_LOCALSTORAGE_KEY, JSON.stringify(addressToUse));
      }

      // if webhookUrl is present, send the payload to webhook
      if (webhookUrl) {
        const webhookPayload = {
          address: visibleFields.address ? addressToUse : undefined,
          ...formState.getValues()
        };
        try {
          // SEND PAYLOAD TO Make hook
          await fetch(webhookUrl, {
            method: "POST",
            mode: "no-cors",
            headers: {
              "Content-Type": "application/json"
            },
            body: JSON.stringify(webhookPayload)
          });

          if (!redirectToQuiz || !redirectSlug) {
            setIsLoading(false);
          }
        } catch (err) {
          setIsLoading(false);
          logError({
            error: err as Error,
            source: "Tile - LeadForm - handleClick",
            message: "Error sending contactForm info to webhook",
            type: ERROR_TYPES.CLIENT_TILE,
            url: window.location.href,
            additionalInfo: {
              webhookUrl: webhookUrl,
              payload: webhookPayload
            }
          });
          return;
        }
      }

      if (redirectToQuiz && quizSlug) {
        router.push(`/${getAppStringForLanguage(APP_STRINGS_LABELS.QUIZ, language)}/${quizSlug}`);
      } else if (redirectSlug) {
        toast.success("Redirecting...");
        const redirectUrl = redirectSlug.startsWith("/") ? redirectSlug : `/${redirectSlug}`;
        router.push(redirectUrl);
      }
    },
    [
      address,
      formState,
      logError,
      router,
      visibleFields,
      webhookUrl,
      isLoading,
      quizSlug,
      redirectToQuiz,
      onlyAddressFieldVisible,
      language,
      redirectSlug
    ]
  );

  const handleUpdateAddress = useCallback(
    (newAddress: AddressAnswer) => {
      setAddress(newAddress);
      const hasError = !Object.values(newAddress).some((val) => !!val);
      setHasAddressError(hasError);
      if (!hasError && onlyAddressFieldVisible) {
        handleClick(undefined, newAddress);
      }
    },
    [handleClick, onlyAddressFieldVisible]
  );

  return (
    <>
      {visibleFields.address && (
        <AddressAutocomplete
          address={address}
          updateAddress={handleUpdateAddress}
          hasError={!onlyAddressFieldVisible ? hasAddressError : false}
          language={language}
        />
      )}
      {!onlyAddressFieldVisible && (
        <Form {...formState}>
          <div className="mx-auto grid w-full grid-cols-1 gap-2 px-2 lg:gap-3 lg:px-0">
            {visibleFields.name && (
              <div className="grid grid-cols-2 gap-x-3">
                <FormField
                  control={formState.control}
                  name="firstName"
                  render={({ field, fieldState }) => (
                    <TextFieldInput
                      label={getAppStringForLanguage(APP_STRINGS_LABELS.FIRST_NAME, language)}
                      field={field}
                      fieldState={fieldState}
                      data-testid="FirstNameInput"
                    />
                  )}
                />
                <FormField
                  control={formState.control}
                  name="lastName"
                  render={({ field, fieldState }) => (
                    <TextFieldInput
                      label={getAppStringForLanguage(APP_STRINGS_LABELS.LAST_NAME, language)}
                      field={field}
                      fieldState={fieldState}
                      data-testid="LastNameInput"
                    />
                  )}
                />
              </div>
            )}
            {visibleFields.email && (
              <FormField
                control={formState.control}
                name="email"
                render={({ field, fieldState }) => (
                  <TextFieldInput
                    label={getAppStringForLanguage(APP_STRINGS_LABELS.EMAIL, language)}
                    field={field}
                    fieldState={fieldState}
                  />
                )}
              />
            )}

            {visibleFields.phone && (
              <FormField
                control={formState.control}
                name="phone"
                render={({ field, fieldState }) => (
                  <PhoneFieldInput
                    label={getAppStringForLanguage(APP_STRINGS_LABELS.PHONE_NUMBER, language)}
                    field={field}
                    fieldState={fieldState}
                    maxLength={14}
                  />
                )}
              />
            )}
          </div>
        </Form>
      )}

      {showTermsMessage && (
        <p className="items-center text-left font-matter text-sm font-normal text-base-primary dark:text-base-dark-disabled ">
          {getAppStringForLanguage(APP_STRINGS_LABELS.TNC_MESSAGE_PART_1, language)}{" "}
          <UIButton className="h-auto gap-1 !p-0 text-sm" asChild variant={ButtonVariantTypes.LINK_EDIT}>
            <Link href="/privacy-policy" target="_blank">
              {" "}
              {getAppStringForLanguage(APP_STRINGS_LABELS.PRIVACY_POLICY, language)}
            </Link>
          </UIButton>{" "}
          {getAppStringForLanguage(APP_STRINGS_LABELS.AND, language)}{" "}
          <UIButton className="h-auto gap-1 !p-0 text-sm" variant={ButtonVariantTypes.LINK_EDIT} asChild>
            <Link href="/terms-of-service" target="_blank">
              {" "}
              {getAppStringForLanguage(APP_STRINGS_LABELS.TERMS_OF_SERVICE, language)}
            </Link>
          </UIButton>{" "}
          {getAppStringForLanguage(APP_STRINGS_LABELS.FOR_MORE_INFORMATION, language)}
        </p>
      )}

      <Button
        className="!my-1 w-fit"
        size={isMobile ? "xl" : "xlb"}
        onClick={onlyAddressFieldVisible ? handleClick : formState.handleSubmit(() => handleClick())}
      >
        {isLoading && <Loader className="!h-6 !w-6" wrapperClassName="mr-2" />}
        {buttonText}
      </Button>
    </>
  );
};

export default LeadForm;
